import type { Block } from "@/lib/api";

/**
 * The merge a 409 rebase makes: local blocks, the server's blocks, and what
 * each side looked like at the last sync.
 *
 * The server's batch save replaces the whole document — a block missing from
 * the payload is deleted. So the merge decides the next payload, and it used to
 * be "every local block, plus the server's new ones". That lost two things:
 *
 * - a block deleted elsewhere came back (the local copy was pushed again), and
 *   a block this editor deleted came back too (the server's copy was re-added);
 * - a block converted or edited elsewhere was overwritten by this editor's
 *   untouched copy.
 *
 * With the last-synced state as a base, each block is a three-way decision:
 * whoever changed it since the last sync wins; if both did, this editor wins
 * (the author is looking at it) and the collision is reported. Without a base
 * — nothing synced yet — it falls back to the old union, which loses nothing
 * the author typed.
 *
 * Two bases, because the two sides are written differently: the editor's
 * serializer and the server's normaliser produce different JSON for the same
 * block. `localBase` is what this editor last pushed, in its own format;
 * `serverBase` is what the server last returned, in its format. Each side is
 * only ever compared with its own.
 */

export interface RebaseBases {
  /** id → signature of the block as this editor last pushed it. */
  local: ReadonlyMap<string, string>;
  /** id → signature of the block as the server last returned it. */
  server: ReadonlyMap<string, string>;
}

export interface RebaseResult {
  merged: Block[];
  /** Server blocks to add to the canvas. */
  inserted: Block[];
  /** Local blocks the server deleted and this editor never touched. */
  removed: string[];
  /** Server versions replacing untouched local blocks (a remote edit or conversion). */
  replaced: Block[];
  /** Blocks both sides changed; this editor's version was kept. */
  collisions: string[];
}

/** Key-order-independent JSON, so two equal blocks always sign the same. */
function stable(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stable(obj[k])}`)
    .join(",")}}`;
}

/** What counts as "this block changed": its type and content. Order is merged separately. */
export function blockSignature(block: Pick<Block, "type" | "content">): string {
  return stable({ type: block.type, content: block.content ?? null });
}

export function signatures(blocks: readonly Block[]): Map<string, string> {
  return new Map(blocks.map((b) => [b.id, blockSignature(b)]));
}

export function mergeOnRebase(
  localBlocks: readonly Block[],
  serverBlocks: readonly Block[],
  bases: RebaseBases | null,
): RebaseResult {
  const localById = new Map(localBlocks.map((b) => [b.id, b]));
  const serverById = new Map(serverBlocks.map((b) => [b.id, b]));

  // No base yet: the old union. Keeps everything the author has, adds the rest.
  if (!bases) {
    const inserted = serverBlocks.filter((b) => !localById.has(b.id));
    return {
      merged: sortByOrder([...localBlocks, ...inserted]),
      inserted,
      removed: [],
      replaced: [],
      collisions: [],
    };
  }

  const localChanged = (b: Block) => bases.local.get(b.id) !== blockSignature(b);
  const serverChanged = (b: Block) => {
    const was = bases.server.get(b.id);
    // A server block we have no record of is not "changed by someone else" —
    // not knowing is not evidence, and the safe reading keeps the local copy.
    return was !== undefined && was !== blockSignature(b);
  };

  const merged: Block[] = [];
  const removed: string[] = [];
  const replaced: Block[] = [];
  const collisions: string[] = [];

  for (const local of localBlocks) {
    const server = serverById.get(local.id);
    if (!server) {
      // Gone from the server. Deleted elsewhere if the server had it at the
      // last sync and nobody here has touched it since; otherwise it is new
      // here, or edited here, and it stays.
      if (bases.server.has(local.id) && !localChanged(local)) removed.push(local.id);
      else merged.push(local);
      continue;
    }
    const here = localChanged(local);
    const there = serverChanged(server);
    if (there && !here) {
      // Converted or edited elsewhere; untouched here — take theirs, keep our position.
      const taken = { ...server, sortOrder: local.sortOrder };
      replaced.push(taken);
      merged.push(taken);
    } else {
      if (there && here) collisions.push(local.id);
      merged.push(local);
    }
  }

  // Server blocks this editor doesn't have: new elsewhere, unless this editor
  // had it at the last sync and has since deleted it.
  const inserted = serverBlocks.filter((b) => !localById.has(b.id) && !bases.local.has(b.id));
  merged.push(...inserted);

  return { merged: sortByOrder(merged), inserted, removed, replaced, collisions };
}

function sortByOrder(blocks: Block[]): Block[] {
  return [...blocks].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}
